<template>
  <div class="peaks-sections">
    <!-- The whole tab bar only exists when there is a second tab to reach.
         A lone "Details" tab is chrome that does nothing. -->
    <div v-if="summitingHikes.length > 0" class="peaks-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        class="peaks-tab"
        :class="{ 'peaks-tab--active': activeTab === 'details' }"
        :aria-selected="activeTab === 'details'"
        @click="activeTab = 'details'"
      >Details</button>
      <button
        type="button"
        role="tab"
        class="peaks-tab"
        :class="{ 'peaks-tab--active': activeTab === 'hikes' }"
        :aria-selected="activeTab === 'hikes'"
        @click="activeTab = 'hikes'"
      >Hikes</button>
    </div>

    <!-- ── Details ─────────────────────────────────────────────────── -->
    <template v-if="activeTab === 'details'">
      <div class="peaks-section">
        <h3 class="peaks-section__title">Peak bagging</h3>
        <div
          class="peaks-bag-state"
          :class="peak.bagged ? 'peaks-bag-state--bagged' : 'peaks-bag-state--todo'"
        >
          <span class="material-icons">{{ peak.bagged ? 'check_circle' : 'radio_button_unchecked' }}</span>
          <template v-if="peak.bagged">
            <span>Bagged on {{ formatBaggedDate(peak.baggedPeak?.firstBaggedAt) }}</span>
            <a
              v-if="peak.baggedPeak?.recording?.id"
              class="peaks-bag-state__link"
              :href="`/user/recordings/${peak.baggedPeak.recording.id}`"
            >View Recording</a>
          </template>
          <span v-else-if="signedIn">Not bagged yet</span>
          <a v-else :href="signInPath" class="peaks-bag-state__link">Sign in to track this peak</a>

          <!-- On every state, not just the unbagged one. Summits are bagged
               automatically from recorded hikes, so "I never marked this" is the
               question a bagged peak raises — and the phrase itself is new to
               most people meeting it on a map. -->
          <button
            type="button"
            class="peaks-info-btn"
            aria-label="What is peak bagging?"
            @click="infoOpen = true"
          >
            <span class="material-icons">info</span>
          </button>
        </div>

        <!-- Every list this peak is on, with the viewer's progress. -->
        <div v-if="peakListsLoading" class="peaks-section__state">Loading lists…</div>
        <div v-else-if="peakLists.length" class="peaks-list-cards">
          <a
            v-for="entry in peakLists"
            :key="entry.id"
            class="peaks-list-card"
            :href="peakListPath(entry)"
            :style="{ borderColor: entry.highlightColor || UNBAGGED_COLOR }"
          >
            <span class="peaks-list-card__count">
              {{ entry.baggedCount || 0 }}/{{ entry.peaksCount }}
            </span>
            <span class="peaks-list-card__name">{{ entry.name }}</span>
            <span class="peaks-list-card__track">
              <span
                class="peaks-list-card__fill"
                :style="{
                  width: `${progressPercent(entry.baggedCount || 0, entry.peaksCount)}%`,
                  background: entry.highlightColor || BAGGED_COLOR
                }"
              ></span>
            </span>
          </a>
        </div>
      </div>

      <dl class="peaks-facts">
        <div v-if="peak.prominenceM != null" class="peaks-fact">
          <dt>Prominence</dt>
          <dd>{{ formatHeight(peak.prominenceM) }}</dd>
        </div>
        <div class="peaks-fact">
          <dt>Height</dt>
          <dd>
            {{ formatHeightPair(peak.heightM) }}
            <span v-if="peak.elevationSource === 'dem'" class="peaks-fact__note">estimated</span>
          </dd>
        </div>
        <div v-if="rankLabel" class="peaks-fact">
          <dt>Rank on list</dt>
          <dd>{{ rankLabel }}</dd>
        </div>
        <div v-if="peak.dobihNumber" class="peaks-fact">
          <dt>DoBIH number</dt>
          <dd>{{ peak.dobihNumber }}</dd>
        </div>
      </dl>

      <p v-if="altNames" class="peaks-detail-alt">Also known as {{ altNames }}</p>
      <p v-if="descriptionText" class="peaks-detail-description">{{ descriptionText }}</p>

    <!-- The host drops its own place-data block in here: the sheet on the
         maps and the peak panel each style it to their own chrome. -->
    <slot name="extra" />

    </template>

    <!-- ── Hikes ───────────────────────────────────────────────────── -->
    <template v-else>
      <section class="peaks-section">
        <h3 class="peaks-section__title">Hikes that summit</h3>

        <div v-if="hikesLoading" class="peaks-section__state">Loading routes…</div>
        <div v-else-if="hikesError" class="peaks-section__state">{{ hikesError }}</div>

        <ul class="peaks-hikes__list">
          <li v-for="entry in summitingHikes" :key="entry.hike.id">
            <a class="peaks-hike-card" :href="entry.hike.canonicalUrl || '#'">
              <img
                v-if="entry.hike.primaryImageUrl"
                :src="entry.hike.primaryImageUrl"
                :alt="entry.hike.name"
                class="peaks-hike-card__image"
                loading="lazy"
              />
              <span class="peaks-hike-card__body">
                <span class="peaks-hike-card__name">
                  <span class="material-icons peaks-hike-card__verified">verified</span>
                  {{ entry.hike.name }}
                </span>
                <span class="peaks-hike-card__stats">
                  <template v-if="formatRating(entry.hike.averageRating, entry.hike.ratingsCount)">
                    <span class="material-icons peaks-hike-card__star">star</span>
                    {{ formatRating(entry.hike.averageRating, entry.hike.ratingsCount) }} &middot;
                  </template>
                  <span
                    v-if="entry.hike.difficulty"
                    :style="{ color: difficultyColor(entry.hike.difficulty) }"
                  >{{ entry.hike.difficulty }}</span>
                  <template v-if="entry.hike.difficulty"> &middot; </template>
                  {{ formatRouteDistance(entry.hike.distanceMeters, metric) }}
                  <template v-if="entry.hike.elevationGainMeters">
                    &middot; {{ formatAscent(entry.hike.elevationGainMeters, metric) }}
                  </template>
                </span>
                <span v-if="otherPeaksLabel(entry.otherPeaksCount)" class="peaks-hike-card__summits">
                  <span class="material-icons">terrain</span>
                  {{ otherPeaksLabel(entry.otherPeaksCount) }}
                </span>
              </span>
            </a>
          </li>
        </ul>
      </section>
    </template>

    <!-- Fixed rather than nested: the panel this sits in clips its overflow, so
         an explainer drawn inside it would be cut off. -->
    <transition name="peaks-detail">
      <div
        v-if="infoOpen"
        class="peaks-info-modal"
        role="dialog"
        aria-modal="true"
        aria-label="What is peak bagging?"
        @click.self="infoOpen = false"
      >
        <div class="peaks-info-modal__card">
          <button
            type="button"
            class="peaks-info-modal__close"
            aria-label="Close"
            @click="infoOpen = false"
          >
            <span class="material-icons">close</span>
          </button>

          <h3 class="peaks-info-modal__title">What is peak bagging?</h3>
          <p class="peaks-info-modal__lead">Pick a list of summits. Climb them. Tick them off.</p>
          <p class="peaks-info-modal__body">
            Peak bagging turns a mountain range into a challenge you can finish — the Munros, the
            Wainwrights, the Arderins, and more of the world's great hill lists all the time.
          </p>

          <ol class="peaks-info-modal__steps">
            <li><span class="material-icons">format_list_bulleted</span><span>Choose a list</span></li>
            <li><span class="material-icons">hiking</span><span>Record your hike</span></li>
            <li><span class="material-icons">check_circle</span><span>The summit bags itself</span></li>
          </ol>

          <p class="peaks-info-modal__body">
            Record a hike with HiiKER and every listed summit you cross is bagged automatically. No
            checking in, no paperwork — just walk, and watch the list fill up.
          </p>

          <h4 class="peaks-info-modal__subtitle">Already ticked something you never marked?</h4>
          <p class="peaks-info-modal__body">
            That is the automatic bagging working backwards. We check the hikes you have already
            recorded, so summits you crossed before you found the list are ticked off too. Nothing to
            undo — it just means you have been there.
          </p>

          <a class="peaks-info-modal__link" href="/peaks">Explore the peak lists</a>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref'
import { postGraphql } from './api'
import { HIKES_SUMMITING_PEAK_QUERY, PEAK_LISTS_FOR_PEAK_QUERY } from './queries'
import {
  BAGGED_COLOR,
  UNBAGGED_COLOR,
  difficultyColor,
  formatAscent,
  formatBaggedDate,
  formatHeight,
  formatHeightPair,
  formatRating,
  formatRouteDistance,
  otherPeaksLabel,
  peakListPath,
  plainDescription,
  progressPercent,
  sortPeakLists,
  type ListPeak,
  type PeakListProgress,
  type SummitingHike
} from './helpers'

/**
 * Everything that is only true of a peak: the bagging state, the lists the
 * summit sits on, its facts, and the hikes that climb it.
 *
 * Shared by the peak panel on the list maps and the POI sheet on every other
 * map, so a summit opened from the POI layer shows exactly what the same summit
 * shows on its list's map. A hotel or a water source renders none of this —
 * the host simply does not mount it.
 *
 * It loads its own data from the peak's id. The hikes are a spatial query that
 * costs a second or two per peak on production, and the lists are a per-amenity
 * association, so neither is worth asking for until a summit is actually open.
 */
const props = withDefaults(defineProps<{
  peak: ListPeak
  signedIn: boolean
  signInPath: string
  rankLabel?: string | null
}>(), { rankLabel: null })

type TabName = 'details' | 'hikes'

const activeTab = ref<TabName>('details')
const infoOpen = ref(false)

// Keyed by peak id and never evicted: each entry is a dozen small objects, so
// re-opening a summit looked at a minute ago costs nothing.
const hikesByPeak = ref<Record<string, SummitingHike[]>>({})
const hikesLoading = ref(false)
const hikesError = ref('')

const peakListsByPeak = ref<Record<string, PeakListProgress[]>>({})
const peakListsLoading = ref(false)

const metric = MeasurementPrefUtility.preference().isMetric()

const peakId = computed(() => props.peak.id)
const summitingHikes = computed(() => hikesByPeak.value[peakId.value] || [])
const peakLists = computed(() => sortPeakLists(peakListsByPeak.value[peakId.value] || []))
const altNames = computed(() => (props.peak.altNames || []).join(', '))
const descriptionText = computed(() => plainDescription(props.peak.description))

// Escape closes the explainer before anything else acts on it, so dismissing it
// does not also close the panel the reader was reading.
function onInfoKeydown (event: KeyboardEvent): void {
  if (event.key !== 'Escape' || !infoOpen.value) return
  event.stopPropagation()
  infoOpen.value = false
}

onMounted(() => window.addEventListener('keydown', onInfoKeydown, true))
onBeforeUnmount(() => window.removeEventListener('keydown', onInfoKeydown, true))

// Details every time: a peak with no hikes has no Hikes tab to land on, and
// carrying the previous peak's tab over would show an empty panel.
watch(peakId, (id) => {
  activeTab.value = 'details'
  infoOpen.value = false
  hikesError.value = ''
  hikesLoading.value = false
  peakListsLoading.value = false
  load(id)
}, { immediate: true })

/** The place id the host needs to look the summit up with, once we have it. */
const emit = defineEmits<{ (event: 'place-id', placeId: string | null): void }>()

function load (id: string): void {
  if (!id) return
  loadSummitingHikes(id)
  loadPeakLists(id)
}

async function loadSummitingHikes (id: string): Promise<void> {
  if (hikesByPeak.value[id]) return

  hikesLoading.value = true
  hikesError.value = ''
  try {
    const data = await postGraphql<{ hikesSummitingPeak: SummitingHike[] }>(
      HIKES_SUMMITING_PEAK_QUERY, { amenityId: id, limit: 12 }
    )
    hikesByPeak.value = { ...hikesByPeak.value, [id]: data.hikesSummitingPeak || [] }
  } catch (e) {
    // Only surface it if this peak is still the one on screen — a stale
    // response for a summit the reader has moved on from should not put an
    // error in front of them.
    if (peakId.value === id) hikesError.value = e instanceof Error ? e.message : 'Could not load routes'
  } finally {
    if (peakId.value === id) hikesLoading.value = false
  }
}

async function loadPeakLists (id: string): Promise<void> {
  if (peakListsByPeak.value[id]) return

  peakListsLoading.value = true
  try {
    const data = await postGraphql<{ amenityById: { placeId: string | null, peakLists: PeakListProgress[] } | null }>(
      PEAK_LISTS_FOR_PEAK_QUERY, { id }
    )
    peakListsByPeak.value = { ...peakListsByPeak.value, [id]: data.amenityById?.peakLists || [] }
    emit('place-id', data.amenityById?.placeId || null)
  } catch {
    // The lists are a nice-to-have beside the summit's own facts — a failure
    // here should leave the rest of the panel alone.
    peakListsByPeak.value = { ...peakListsByPeak.value, [id]: [] }
  } finally {
    if (peakId.value === id) peakListsLoading.value = false
  }
}
</script>
